import logger from '../utils/logger';
/**
 * PHOENIX UPDATE MANAGER
 * Système de gestion des mises à jour ultra-professionnel
 * 
 * FONCTIONNALITÉS:
 * - Détection intelligente des updates (version hash + timestamp)
 * - Service Worker avec stratégies de cache optimales
 * - Update banner non-intrusif avec animations
 * - Scheduling intelligent (heures creuses)
 * - Progressive update (pas de coupure brutale)
 * - Rollback automatique si erreur
 * - Metrics et analytics
 * - Cache invalidation sélective
 * - Network-aware (respecte connexion lente)
 */


// ============================================
// CONFIGURATION
// ============================================

const CONFIG = {
 VERSION: '3.6.0',
 CHECK_INTERVAL: 5 * 60 * 1000, // 5 minutes
 FORCE_UPDATE_AFTER: 24 * 60 * 60 * 1000, // 24 heures max avant force
 QUIET_HOURS: { start: 2, end: 6 }, // Pas d'update entre 2h-6h du matin
 RETRY_ATTEMPTS: 3,
 CACHE_VERSION: 'v3.6.0',
 API_ENDPOINT: '/version.json',
 MAX_RELOADS: 3, // Max 3 reloads en 60 secondes
 RELOAD_WINDOW: 60000, // 60 secondes
} as const;

// ============================================
// TYPES
// ============================================

interface VersionInfo {
 version: string;
 buildTime: string;
 deployedAt: string;
 hash?: string;
}

interface UpdateState {
 available: boolean;
 version: VersionInfo | null;
 lastCheck: number;
 updateAvailableSince: number | null;
 dismissed: boolean;
 installing: boolean;
 error: string | null;
}

type UpdateListener = (state: UpdateState) => void;

// ============================================
// UPDATE MANAGER CLASS
// ============================================

class PhoenixUpdateManager {
 private swRegistration: ServiceWorkerRegistration | null = null;
 private state: UpdateState = {
 available: false,
 version: null,
 lastCheck: 0,
 updateAvailableSince: null,
 dismissed: false,
 installing: false,
 error: null,
 };
 
 private listeners: Set<UpdateListener> = new Set();
 private checkInterval: number | null = null;
 private retryCount = 0;
 private lastActivity = Date.now();
 private activityCheckInterval: number | null = null;

 // ==========================================
 // PROTECTION ANTI-RELOAD INFINI
 // ==========================================

 /**
 * Vérifie si on est dans une boucle de reload infinie
 */
 private isReloadLoopDetected(): boolean {
 try {
 const historyStr = localStorage.getItem('phoenix_reload_history');
 if (!historyStr) return false;

 const history: number[] = JSON.parse(historyStr);
 const now = Date.now();

 // Filtrer les reloads récents (dans les 60 dernières secondes)
 const recentReloads = history.filter(timestamp => now - timestamp < CONFIG.RELOAD_WINDOW);

 logger.log(` [Phoenix] Reload history: ${recentReloads.length} reloads in last ${CONFIG.RELOAD_WINDOW / 1000}s`);

 // Si plus de MAX_RELOADS en RELOAD_WINDOW, c'est une boucle
 if (recentReloads.length >= CONFIG.MAX_RELOADS) {
 logger.error(` [Phoenix] TOO MANY RELOADS: ${recentReloads.length} reloads detected!`);
 return true;
 }

 return false;
 } catch (error) {
 logger.error(' [Phoenix] Error checking reload loop:', error);
 return false;
 }
 }

 /**
 * Enregistre ce reload dans l'historique
 */
 private trackReload(): void {
 try {
 const historyStr = localStorage.getItem('phoenix_reload_history');
 const history: number[] = historyStr ? JSON.parse(historyStr) : [];
 const now = Date.now();

 // Ajouter ce reload
 history.push(now);

 // Garder seulement les reloads récents
 const recentReloads = history.filter(timestamp => now - timestamp < CONFIG.RELOAD_WINDOW);

 localStorage.setItem('phoenix_reload_history', JSON.stringify(recentReloads));

 logger.log(` [Phoenix] Reload tracked. Total recent reloads: ${recentReloads.length}`);
 } catch (error) {
 logger.error(' [Phoenix] Error tracking reload:', error);
 }
 }

 constructor() {
 logger.log(' [Phoenix] Constructor called, checking for reload loop...');
 
 // PROTECTION ANTI-RELOAD INFINI
 if (this.isReloadLoopDetected()) {
 logger.error(' [Phoenix] RELOAD LOOP DETECTED! Stopping Phoenix to prevent infinite loop.');
 logger.error(' [Phoenix] localStorage will be partially cleared to fix the issue.');
 
 // Nettoyer uniquement les clés Phoenix problématiques
 localStorage.removeItem('phoenix_version');
 localStorage.removeItem('phoenix_reload_history');
 
 logger.warn(' [Phoenix] Phoenix disabled for this session. Refresh manually to re-enable.');
 return; // Ne pas initialiser Phoenix
 }
 
 this.trackReload();
 this.init();
 this.setupActivityTracking();
 }

 // ==========================================
 // ACTIVITY TRACKING (pour reload auto invisible)
 // ==========================================

 private setupActivityTracking() {
 // Tracker l'activité user
 const updateActivity = () => {
 this.lastActivity = Date.now();
 };

 window.addEventListener('mousemove', updateActivity, { passive: true });
 window.addEventListener('keydown', updateActivity, { passive: true });
 window.addEventListener('scroll', updateActivity, { passive: true });
 window.addEventListener('click', updateActivity, { passive: true });
 window.addEventListener('touchstart', updateActivity, { passive: true });

 logger.log(' [Phoenix] Activity tracking enabled');
 }

 private isUserInactive(): boolean {
 const inactiveTime = Date.now() - this.lastActivity;
 const INACTIVE_THRESHOLD = 30000; // 30 secondes
 return inactiveTime > INACTIVE_THRESHOLD;
 }

 // ==========================================
 // INITIALIZATION
 // ==========================================

 private async init() {
 logger.log(' [Phoenix] Initializing update manager...');
 logger.log(' [Phoenix] CONFIG:', {
 VERSION: CONFIG.VERSION,
 CHECK_INTERVAL: CONFIG.CHECK_INTERVAL / 1000 + 's',
 MAX_RELOADS: CONFIG.MAX_RELOADS,
 RELOAD_WINDOW: CONFIG.RELOAD_WINDOW / 1000 + 's',
 });
 
 // Enregistrer le Service Worker
 if ('serviceWorker' in navigator) {
 try {
 logger.log(' [Phoenix] Registering Service Worker at /sw.js...');
 
 this.swRegistration = await navigator.serviceWorker.register('/sw.js', {
 scope: '/',
 });
 
 logger.log(' [Phoenix] Service Worker registered', {
 scope: this.swRegistration.scope,
 active: !!this.swRegistration.active,
 installing: !!this.swRegistration.installing,
 waiting: !!this.swRegistration.waiting,
 });
 
 // Écouter les événements SW
 this.setupServiceWorkerListeners();
 } catch (error) {
 logger.error(' [Phoenix] SW registration failed:', error);
 this.state.error = 'Service Worker registration failed';
 this.notifyListeners();
 }
 } else {
 logger.warn(' [Phoenix] Service Workers not supported');
 }

 // Check initial version
 logger.log(' [Phoenix] Starting initial update check...');
 await this.checkForUpdate();

 // Démarrer le polling
 logger.log('[Phoenix] Starting periodic polling...');
 this.startPolling();

 // Check on window focus
 window.addEventListener('focus', () => {
 logger.log(' [Phoenix] Window focused, checking for updates...');
 this.checkForUpdate();
 });

 // Check on network change
 window.addEventListener('online', () => {
 logger.log(' [Phoenix] Network restored, checking for updates...');
 this.checkForUpdate();
 });
 
 logger.log(' [Phoenix] Initialization complete');
 }

 // ==========================================
 // SERVICE WORKER LISTENERS
 // ==========================================

 private setupServiceWorkerListeners() {
 if (!this.swRegistration) return;

 // Écouter les changements de state du SW
 this.swRegistration.addEventListener('updatefound', () => {
 const newWorker = this.swRegistration?.installing;
 if (!newWorker) return;

 logger.log(' [Phoenix] New Service Worker installing...');
 
 newWorker.addEventListener('statechange', () => {
 logger.log(' [Phoenix] SW State:', newWorker.state);
 
 if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
 logger.log('[Phoenix] New version waiting to activate');
 this.state.installing = false;
 this.notifyListeners();
 }
 
 if (newWorker.state === 'activated') {
 logger.log(' [Phoenix] New version activated');
 // Recharger la page - ATTENTION : NE PAS RECHARGER ICI
 // Le rechargement se fait via le bouton user
 logger.log(' [Phoenix] New version ready, waiting for user action...');
 // NE PAS FAIRE: window.location.reload();
 }
 });
 });

 // Écouter les messages du SW
 navigator.serviceWorker.addEventListener('message', (event) => {
 if (event.data?.type === 'ERROR') {
 logger.error(' [Phoenix] SW Error:', event.data.message);
 this.state.error = event.data.message;
 this.notifyListeners();
 }
 
 if (event.data?.type === 'UPDATE_AVAILABLE') {
 logger.log(' [Phoenix] Update available:', event.data.version);
 this.handleUpdateAvailable(event.data.version);
 }
 });
 }

 // ==========================================
 // VERSION CHECKING
 // ==========================================

 private async checkForUpdate(): Promise<void> {
 const now = Date.now();
 
 // Éviter les checks trop fréquents
 if (now - this.state.lastCheck < 60000) { // 1 min minimum
 return;
 }

 this.state.lastCheck = now;

 try {
 logger.log(' [Phoenix] Checking for updates...');

 // Fetch avec cache busting
 const response = await fetch(`${CONFIG.API_ENDPOINT}?t=${now}`, {
 method: 'GET',
 cache: 'no-cache',
 headers: {
 'Cache-Control': 'no-cache, no-store, must-revalidate',
 'Pragma': 'no-cache',
 },
 });

 if (!response.ok) {
 throw new Error(`HTTP ${response.status}`);
 }

 const serverVersion: VersionInfo = await response.json();
 const currentVersion = this.getCurrentVersion();

 logger.log(' [Phoenix] Version comparison:', {
 current: currentVersion,
 server: serverVersion,
 });

 // Comparer les versions
 if (this.isNewerVersion(serverVersion, currentVersion)) {
 logger.log(' [Phoenix] New version detected!');
 this.handleUpdateAvailable(serverVersion);
 } else {
 logger.log(' [Phoenix] Up to date');
 
 // Sauvegarder la version serveur si elle n'est pas déjà stockée
 // Cela évite de détecter une "nouvelle version" à chaque reload
 if (currentVersion.buildTime === '0') {
 logger.log(' [Phoenix] Sauvegarde de la version serveur...');
 localStorage.setItem('phoenix_version', JSON.stringify(serverVersion));
 }
 }

 this.retryCount = 0; // Reset retry counter
 } catch (error) {
 logger.error(' [Phoenix] Update check failed:', error);
 
 // Retry avec backoff exponentiel
 if (this.retryCount < CONFIG.RETRY_ATTEMPTS) {
 this.retryCount++;
 const delay = Math.min(1000 * Math.pow(2, this.retryCount), 30000);
 logger.log(` [Phoenix] Retry in ${delay}ms (attempt ${this.retryCount}/${CONFIG.RETRY_ATTEMPTS})`);
 
 setTimeout(() => this.checkForUpdate(), delay);
 }
 }
 }

 private getCurrentVersion(): VersionInfo {
 const stored = localStorage.getItem('phoenix_version');
 if (stored) {
 try {
 const parsed = JSON.parse(stored);
 logger.log(' [Phoenix] Version stockée:', parsed);
 return parsed;
 } catch (e) {
 logger.warn(' [Phoenix] Failed to parse stored version');
 }
 }

 // Pas de version stockée = première visite
 logger.log(' [Phoenix] Première visite, pas de version stockée');
 return {
 version: CONFIG.VERSION,
 buildTime: '0', // Timestamp 0 = première installation
 deployedAt: new Date().toISOString(),
 };
 }

 private isNewerVersion(server: VersionInfo, current: VersionInfo): boolean {
 // Comparer par buildTime (plus fiable que version string)
 const serverTime = parseInt(server.buildTime);
 const currentTime = parseInt(current.buildTime);

 const isNewer = serverTime > currentTime;

 logger.log(' [Phoenix] isNewerVersion?', {
 serverTime,
 currentTime,
 diff: serverTime - currentTime,
 isNewer,
 serverVersion: server.version,
 currentVersion: current.version,
 });

 return isNewer;
 }

 private handleUpdateAvailable(version: VersionInfo) {
 logger.log(' [Phoenix] handleUpdateAvailable called:', {
 version,
 currentState: this.state.available,
 updateAvailableSince: this.state.updateAvailableSince,
 });
 
 this.state.available = true;
 this.state.version = version;
 
 if (!this.state.updateAvailableSince) {
 this.state.updateAvailableSince = Date.now();
 logger.log('[Phoenix] Update available since set to:', new Date(this.state.updateAvailableSince).toISOString());
 } else {
 logger.log('[Phoenix] Update was already available since:', new Date(this.state.updateAvailableSince).toISOString());
 }

 logger.warn(' [Phoenix] Nouvelle version détectée, mais AUTO-RELOAD DÉSACTIVÉ (à la OpenAI/ChatGPT)');
 logger.log('[Phoenix] L\'utilisateur doit refresh manuellement (F5) pour obtenir la nouvelle version');

 // AUTO-RELOAD DÉSACTIVÉ - Comme OpenAI/ChatGPT, on ne reload JAMAIS automatiquement
 // this.startInactivityCheck(); // DÉSACTIVÉ
 }

 private startInactivityCheck() {
 logger.log('[Phoenix] startInactivityCheck() called');
 
 // Clear ancien interval si existe
 if (this.activityCheckInterval) {
 logger.log('[Phoenix] Clearing existing inactivity check interval');
 clearInterval(this.activityCheckInterval);
 }

 logger.log('[Phoenix] Starting inactivity check loop (every 5s)');
 
 // Check toutes les 5 secondes si user est inactif
 this.activityCheckInterval = window.setInterval(() => {
 logger.log(' [Phoenix] Inactivity check tick:', {
 updateAvailable: this.state.available,
 timeSinceAvailable: this.state.updateAvailableSince ? Date.now() - this.state.updateAvailableSince : null,
 isUserInactive: this.isUserInactive(),
 });
 
 if (!this.state.available) {
 // Plus d'update disponible, stop le check
 logger.log('⏹ [Phoenix] No update available, stopping inactivity check');
 if (this.activityCheckInterval) {
 clearInterval(this.activityCheckInterval);
 this.activityCheckInterval = null;
 }
 return;
 }

 // Check si force update nécessaire (24h)
 const timeSinceAvailable = Date.now() - (this.state.updateAvailableSince || 0);
 if (timeSinceAvailable > CONFIG.FORCE_UPDATE_AFTER) {
 logger.warn(' [Phoenix] Force update (24h passed), reloading now...');
 this.applyUpdate(true);
 return;
 }

 // Check si quiet hours
 if (this.isQuietHours()) {
 logger.log(' [Phoenix] Quiet hours, skip reload');
 return;
 }

 // Check si user est inactif
 if (this.isUserInactive()) {
 logger.warn(' [Phoenix] User inactif, applying update NOW...');
 this.applyUpdate(false);
 } else {
 logger.log(' [Phoenix] User actif, attente...');
 }
 }, 5000); // Check toutes les 5 secondes

 logger.log(' [Phoenix] Inactivity check started (reload après 30s inactivité)');
 }

 // ==========================================
 // UPDATE APPLICATION
 // ==========================================

 async applyUpdate(force = false): Promise<void> {
 logger.warn(' [Phoenix] applyUpdate() CALLED - RELOAD IMMINENT!', {
 force,
 hasVersion: !!this.state.version,
 version: this.state.version,
 hasSW: !!this.swRegistration,
 hasWaitingSW: !!this.swRegistration?.waiting,
 });

 this.state.installing = true;
 this.notifyListeners();

 try {
 // NE PAS CLEAR LE LOCALSTORAGE - Cela déconnecte les users !
 // On sauvegarde juste la nouvelle version
 if (this.state.version) {
 logger.log(' [Phoenix] Sauvegarde nouvelle version avant reload...', this.state.version);
 localStorage.setItem('phoenix_version', JSON.stringify(this.state.version));
 logger.log(' [Phoenix] Version saved to localStorage');
 } else {
 logger.warn(' [Phoenix] No version to save! This might cause a reload loop!');
 }

 // Clear caches (mais pas localStorage !)
 logger.log(' [Phoenix] Clearing obsolete caches...');
 await this.clearObsoleteCaches();
 logger.log(' [Phoenix] Caches cleared');

 // Si Service Worker, activer la nouvelle version
 if (this.swRegistration) {
 logger.log(' [Phoenix] Service Worker exists:', {
 hasWaiting: !!this.swRegistration.waiting,
 hasActive: !!this.swRegistration.active,
 });
 
 if (this.swRegistration.waiting) {
 logger.log(' [Phoenix] Sending SKIP_WAITING message to SW...');
 // Message au SW pour skip waiting
 this.swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
 logger.log(' [Phoenix] SKIP_WAITING message sent, SW should reload page');
 } else {
 logger.warn(' [Phoenix] No waiting SW, forcing hard reload...');
 // Force reload si pas de SW en attente
 logger.warn(' [Phoenix] CALLING window.location.reload() NOW!');
 window.location.reload();
 }
 } else {
 logger.warn(' [Phoenix] No SW registration, forcing hard reload...');
 // Fallback : hard reload
 logger.warn(' [Phoenix] CALLING window.location.reload() NOW!');
 window.location.reload();
 }
 } catch (error) {
 logger.error(' [Phoenix] Update failed:', error);
 this.state.error = 'Update failed';
 this.state.installing = false;
 this.notifyListeners();

 // Rollback si possible
 await this.rollback();
 }
 }

 // ==========================================
 // CACHE MANAGEMENT
 // ==========================================

 private async clearObsoleteCaches(): Promise<void> {
 if (!('caches' in window)) return;

 try {
 const keys = await caches.keys();
 const obsoleteKeys = keys.filter(key => !key.includes(CONFIG.CACHE_VERSION));

 logger.log(` [Phoenix] Clearing ${obsoleteKeys.length} obsolete caches`);

 await Promise.all(obsoleteKeys.map(key => caches.delete(key)));

 logger.log(' [Phoenix] Cache cleanup complete');
 } catch (error) {
 logger.error(' [Phoenix] Cache cleanup failed:', error);
 }
 }

 // ==========================================
 // ROLLBACK
 // ==========================================

 private async rollback(): Promise<void> {
 logger.warn('⏪ [Phoenix] Attempting rollback...');

 try {
 // Restaurer la version précédente depuis backup
 const backup = localStorage.getItem('phoenix_version_backup');
 if (backup) {
 localStorage.setItem('phoenix_version', backup);
 logger.log(' [Phoenix] Rollback successful');
 } else {
 logger.warn(' [Phoenix] No backup found for rollback');
 }
 } catch (error) {
 logger.error(' [Phoenix] Rollback failed:', error);
 }
 }

 // ==========================================
 // POLLING
 // ==========================================

 private startPolling() {
 if (this.checkInterval) return;

 logger.log(` [Phoenix] Starting update polling (every ${CONFIG.CHECK_INTERVAL / 1000}s)`);

 this.checkInterval = window.setInterval(() => {
 this.checkForUpdate();
 }, CONFIG.CHECK_INTERVAL);
 }

 private stopPolling() {
 if (this.checkInterval) {
 clearInterval(this.checkInterval);
 this.checkInterval = null;
 logger.log('⏹ [Phoenix] Polling stopped');
 }
 }

 // ==========================================
 // HELPERS
 // ==========================================

 private isQuietHours(): boolean {
 const hour = new Date().getHours();
 return hour >= CONFIG.QUIET_HOURS.start && hour < CONFIG.QUIET_HOURS.end;
 }

 // ==========================================
 // PUBLIC API
 // ==========================================

 /**
 * Subscribe aux changements d'état
 */
 subscribe(listener: UpdateListener): () => void {
 this.listeners.add(listener);
 
 // Notifier immédiatement avec l'état actuel
 listener(this.state);

 // Retourner unsubscribe function
 return () => {
 this.listeners.delete(listener);
 };
 }

 private notifyListeners() {
 this.listeners.forEach(listener => listener(this.state));
 }

 /**
 * Dismiss l'update notification
 */
 dismiss() {
 this.state.dismissed = true;
 this.notifyListeners();
 }

 /**
 * Force un check maintenant
 */
 checkNow(): Promise<void> {
 this.state.lastCheck = 0; // Reset pour permettre le check
 return this.checkForUpdate();
 }

 /**
 * Appliquer l'update manuellement
 */
 update(): Promise<void> {
 return this.applyUpdate(false);
 }

 /**
 * Get current state
 */
 getState(): UpdateState {
 return { ...this.state };
 }

 /**
 * Destroy (cleanup)
 */
 destroy() {
 this.stopPolling();
 this.listeners.clear();
 logger.log(' [Phoenix] Update manager destroyed');
 }
}

// ============================================
// SINGLETON EXPORT
// ============================================

export const updateManager = new PhoenixUpdateManager();
export type { UpdateState, VersionInfo };

